import { useSelector } from "react-redux";
import { selectCartHasEntries, selectIsCartLoading } from "~/features/cart/cart/redux/selectors/cartSelectors";
import type { PageTemplateProps } from "./pageTemplateTypes";

/**
 * React translation of the old {@code pages/quote/schaeffQuotePage.jsp}.
 *
 * The JSP is structurally identical to the cart JSP: the {@code TopContent}
 * slot is the body-bearing include (quote cart React island) and becomes the
 * route body here, per the 0153 body-replacement decision. The remaining CMS
 * slot regions are conditional on quote/cart emptiness, mirroring the JSP
 * {@code c:if test="${not empty cartData.rootGroups}"} branches:
 *
 * - non-empty: {@code CenterLeftContentSlot}, {@code CenterRightContentSlot},
 *   {@code BottomContentSlot};
 * - empty: {@code EmptyCartMiddleContent} (wrapped in the legacy
 *   {@code content__empty} class).
 *
 * The active quote reuses the {@code state.cart} slice (its selectors read
 * {@code state.cart.packageEntriesById}), so {@code selectCartHasEntries} is
 * the named feature selector equivalent to the JSP
 * {@code not empty cartData.rootGroups} condition; no duplicate quote
 * selector is needed. {@code selectIsCartLoading} guards against flashing
 * empty-cart slot content while the body spinner is showing.
 *
 * {@code <cart:cartValidation/>} from the JSP is intentionally ignored (linked
 * usage audit); it disappears by virtue of deleting the JSP.
 */
export function QuoteCartPageTemplate({ slots, body }: PageTemplateProps) {
  const isLoading = useSelector(selectIsCartLoading);
  const hasEntries = useSelector(selectCartHasEntries);
  const showNonEmpty = !isLoading && hasEntries;
  const showEmpty = !isLoading && !hasEntries;

  return (
    <>
      <div>
        <div>{body}</div>
      </div>
      {showNonEmpty && (
        <>
          {slots.renderSlot("CenterLeftContentSlot")}
          {slots.renderSlot("CenterRightContentSlot")}
          {slots.renderSlot("BottomContentSlot")}
        </>
      )}
      {showEmpty && <div className="content__empty">{slots.renderSlot("EmptyCartMiddleContent")}</div>}
    </>
  );
}
